(() => {
  const SELECTOR = '[data-ct-related-section]';

  const getVisibleCount = () => {
    return window.innerWidth >= 990 ? 4 : 2;
  };

  const initSlider = (section) => {
    if (!section || section.dataset.ctSliderReady === 'true') return;

    const viewport = section.querySelector('[data-ct-related-viewport]');
    const track = section.querySelector('[data-ct-related-track]');
    const cards = Array.from(
      section.querySelectorAll('.ct-related__card')
    );

    const prev = section.querySelector('[data-ct-related-prev]');
    const next = section.querySelector('[data-ct-related-next]');

    if (!viewport || !track || cards.length === 0) return;

    section.dataset.ctSliderReady = 'true';

    let index = 0;
    let startX = 0;
    let currentX = 0;
    let baseOffset = 0;
    let dragging = false;
    let moved = false;
    let suppressClick = false;
    let resizeTimer = null;

    const getGap = () => {
      const styles = getComputedStyle(track);

      return parseFloat(
        styles.columnGap ||
        styles.gap ||
        0
      );
    };

    const getMetrics = () => {
      const cardWidth =
        cards[0]?.getBoundingClientRect().width || 0;

      const gap = getGap();
      const visible = getVisibleCount();

      const maxIndex = Math.max(
        0,
        cards.length - visible
      );

      return {
        cardWidth,
        gap,
        step: cardWidth + gap,
        visible,
        maxIndex
      };
    };

    const setTransition = (enabled) => {
      track.style.transition = enabled
        ? 'transform 0.35s ease'
        : 'none';
    };

    const render = (animate = true) => {
      const {
        step,
        visible,
        maxIndex
      } = getMetrics();

      index = Math.max(
        0,
        Math.min(index, maxIndex)
      );

      baseOffset = index * step;

      setTransition(animate);

      track.style.transform =
        `translate3d(${-baseOffset}px, 0, 0)`;

      const hideControls =
        cards.length <= visible;

      if (prev) {
        prev.hidden = hideControls;
        prev.disabled = index <= 0;
      }

      if (next) {
        next.hidden = hideControls;
        next.disabled = index >= maxIndex;
      }
    };

    /* =========================
       ARROWS
    ========================= */

    const goPrev = () => {
      index -= 1;
      render(true);
    };

    const goNext = () => {
      index += 1;
      render(true);
    };

    prev?.addEventListener(
      'click',
      goPrev
    );

    next?.addEventListener(
      'click',
      goNext
    );

    /* =========================
       DRAG START
    ========================= */

    const startDrag = (event) => {
      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }

      const { visible } = getMetrics();

      if (cards.length <= visible) return;

      dragging = true;
      moved = false;

      startX = event.clientX;
      currentX = event.clientX;

      viewport.classList.add(
        'is-dragging'
      );

      setTransition(false);

      /*
       * Prevent native browser image/link dragging.
       */
      if (event.pointerType === 'mouse') {
        event.preventDefault();
      }

      try {
        viewport.setPointerCapture(
          event.pointerId
        );
      } catch (error) {
        // Safe fallback
      }
    };

    /* =========================
       DRAG MOVE
    ========================= */

    const moveDrag = (event) => {
      if (!dragging) return;

      currentX = event.clientX;

      const delta =
        currentX - startX;

      if (Math.abs(delta) > 4) {
        moved = true;
      }

      track.style.transform =
        `translate3d(${(-baseOffset + delta)}px, 0, 0)`;

      if (event.pointerType === 'mouse') {
        event.preventDefault();
      }
    };

    /* =========================
       DRAG END
    ========================= */

    const endDrag = (event) => {
      if (!dragging) return;

      dragging = false;

      viewport.classList.remove(
        'is-dragging'
      );

      const delta =
        currentX - startX;

      const {
        step,
        maxIndex
      } = getMetrics();

      const threshold = Math.min(
        60,
        step * 0.18
      );

      if (Math.abs(delta) >= threshold) {
        if (delta < 0) {
          index = Math.min(
            maxIndex,
            index + 1
          );
        } else {
          index = Math.max(
            0,
            index - 1
          );
        }
      }

      /*
       * Stop accidental product-link click
       * immediately after dragging.
       */
      if (moved) {
        suppressClick = true;

        window.setTimeout(() => {
          suppressClick = false;
        }, 100);
      }

      render(true);

      try {
        if (
          event?.pointerId !== undefined &&
          viewport.hasPointerCapture?.(
            event.pointerId
          )
        ) {
          viewport.releasePointerCapture(
            event.pointerId
          );
        }
      } catch (error) {
        // Safe fallback
      }
    };

    viewport.addEventListener(
      'pointerdown',
      startDrag
    );

    viewport.addEventListener(
      'pointermove',
      moveDrag
    );

    viewport.addEventListener(
      'pointerup',
      endDrag
    );

    viewport.addEventListener(
      'pointercancel',
      endDrag
    );

    /*
     * Disable native image drag.
     */
    viewport.addEventListener(
      'dragstart',
      (event) => {
        event.preventDefault();
      }
    );

    /*
     * Prevent link click after drag.
     */
    viewport.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    /* =========================
       RESIZE
    ========================= */

    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);

        resizeTimer = window.setTimeout(
          () => {
            index = 0;
            render(false);
          },
          120
        );
      }
    );

    render(false);
  };

  /* =========================
     INITIALIZE ALL
  ========================= */

  const initAll = () => {
    document
      .querySelectorAll(SELECTOR)
      .forEach(initSlider);
  };

  let scheduled = false;

  const scheduleInit = () => {
    if (scheduled) return;

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      initAll();
    });
  };

  /* Normal page load */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initAll,
      { once: true }
    );
  } else {
    initAll();
  }

  /*
   * Horizon loads product recommendations
   * asynchronously after initial page load.
   *
   * MutationObserver waits until the real
   * recommendation cards are inserted.
   */
  const observer =
    new MutationObserver(
      scheduleInit
    );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  /* Shopify Theme Editor */
  document.addEventListener(
    'shopify:section:load',
    scheduleInit
  );

  document.addEventListener(
    'shopify:section:reorder',
    scheduleInit
  );
})();